/**
 * FilterPanel — Edit the filters section of a job.
 *
 * One text input per WHERE filter expression. Add/remove controls.
 * Changes call PATCH .../filters.
 */

import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, Loader2 } from "lucide-react";
import type {
  PEJob,
  FilterExpression,
  FiltersPayload,
} from "../../../features/pipeline-engineer/types";
import { patchFilters } from "../../../features/pipeline-engineer/api/pe-client";

// ---------------------------------------------------------------------------
// Internal row type
// ---------------------------------------------------------------------------

interface FilterRow {
  id: string;
  expression: string;
}

function filterToRow(f: FilterExpression, idx: number): FilterRow {
  return { id: `filter-${idx}`, expression: f.expression };
}

function rowsToPayload(rows: FilterRow[]): FiltersPayload {
  return {
    filters: rows.map((r) => ({ expression: r.expression })),
  };
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const INPUT_CLASS =
  "w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-white/30 focus:outline-none focus:border-brand-500/50 focus:ring-1 focus:ring-brand-500/30 font-mono";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface FilterPanelProps {
  job: PEJob;
  onSaved: (updated: PEJob) => void;
  onDirtyChange: (dirty: boolean) => void;
  onConflict: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function FilterPanel({
  job,
  onSaved,
  onDirtyChange,
  onConflict,
}: FilterPanelProps) {
  const [rows, setRows] = useState<FilterRow[]>(() =>
    job.filters.map(filterToRow),
  );
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Compare rows (sans local `id`) against filterToRow output — both camelCase to
  // avoid permanent-dirty from camelCase vs snake_case JSON key mismatch.
  useEffect(() => {
    const pick = (r: FilterRow) => ({ expression: r.expression });
    const original = JSON.stringify(job.filters.map(filterToRow).map(pick));
    const current = JSON.stringify(rows.map(pick));
    onDirtyChange(original !== current);
  }, [rows, job.filters, onDirtyChange]);

  useEffect(() => {
    setRows(job.filters.map(filterToRow));
  }, [job.id, job.updatedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateRow = useCallback((id: string, expression: string) => {
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, expression } : r)),
    );
  }, []);

  const removeRow = useCallback((id: string) => {
    setRows((prev) => prev.filter((r) => r.id !== id));
  }, []);

  const addRow = useCallback(() => {
    setRows((prev) => [
      ...prev,
      { id: `filter-new-${Date.now()}`, expression: "" },
    ]);
  }, []);

  const handleSave = useCallback(async () => {
    setSaveError(null);
    setSaving(true);
    try {
      const updated = await patchFilters(
        job.pipelineId,
        job.id,
        rowsToPayload(rows),
      );
      onSaved(updated);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Save failed.";
      if (msg.includes("409") || msg.toLowerCase().includes("conflict")) {
        onConflict();
      } else {
        setSaveError(msg);
      }
    } finally {
      setSaving(false);
    }
  }, [rows, job.pipelineId, job.id, onSaved, onConflict]);

  return (
    <div className="space-y-4">
      <p className="text-xs text-white/40">
        Each expression is a WHERE clause fragment applied to the source query.
        Expressions are joined with AND.
      </p>

      {rows.length === 0 ? (
        <p className="text-xs text-white/30 py-4 text-center">
          No filters — all rows will be included.
        </p>
      ) : (
        <div className="space-y-2">
          {rows.map((row, idx) => (
            <div key={row.id} className="group flex items-center gap-2">
              <span className="text-xs text-white/30 w-5 text-right flex-shrink-0">
                {idx + 1}
              </span>
              <input
                type="text"
                aria-label={`Filter expression ${idx + 1}`}
                className={INPUT_CLASS}
                placeholder="e.g. o.status != 'test'"
                value={row.expression}
                onChange={(e) => updateRow(row.id, e.target.value)}
              />
              <button
                type="button"
                aria-label={`Remove filter ${idx + 1}`}
                onClick={() => removeRow(row.id)}
                className="opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity p-1.5 text-red-400/60 hover:text-red-400 hover:bg-red-500/10 rounded flex-shrink-0"
              >
                <Trash2 size={13} aria-hidden="true" />
              </button>
            </div>
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={addRow}
        className="flex items-center gap-1.5 px-3 py-2 bg-white/5 hover:bg-white/10 text-white/70 border border-white/10 rounded-lg text-xs font-medium transition-colors w-full justify-center"
      >
        <Plus size={13} aria-hidden="true" />
        Add Filter
      </button>

      {saveError !== null && (
        <p className="text-red-400 text-xs" role="alert">
          {saveError}
        </p>
      )}

      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={saving}
          className="flex items-center gap-1.5 px-4 py-2 bg-brand-500 hover:bg-brand-600 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving && (
            <Loader2 size={12} className="animate-spin" aria-hidden="true" />
          )}
          Save Filters
        </button>
      </div>
    </div>
  );
}
